import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { InputPassword, Input } from '@/components/ui/input'
import { AuthLayout, AuthFormActions, AuthFooterLinks } from '@/components/auth'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/useAuth'
import { useOnboarding } from '@/contexts/OnboardingContext.context'
import { signupSchema, SignupFormValues } from '@/lib/validations/auth'
import { PASSWORD_PLACEHOLDER } from '@/components/ui/input'

const DEMO_SIGNUP_DATA = {
  email: 'demo@example.com',
  password: 'DemoPassword123!',
}

export default function SignupPage() {
  const navigate = useNavigate()
  const { signup } = useAuth()
  const { startOnboarding } = useOnboarding()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
  })

  const passwordValue = watch('password')

  const loadDemoData = () => {
    setValue('email', DEMO_SIGNUP_DATA.email, { shouldValidate: true, shouldDirty: true })
    setValue('password', DEMO_SIGNUP_DATA.password, { shouldValidate: true, shouldDirty: true })
    setValue('confirmPassword', DEMO_SIGNUP_DATA.password, {
      shouldValidate: true,
      shouldDirty: true,
    })

    // Auto-submit the form after a brief delay to allow form validation
    setTimeout(() => {
      handleSubmit(onSubmit)()
    }, 300)

    toast.info('Creating demo account...')
  }

  const onSubmit = async (data: SignupFormValues) => {
    setIsLoading(true)
    try {
      await signup(data)
      toast.success('Account created successfully!')
      // Reset onboarding state for new users
      startOnboarding()
      // Redirect to onboarding for new users
      navigate('/onboarding')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create account')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout title="Sign Up" description="Create a new account to get started">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            {...register('email')}
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium">
            Password
          </label>
          <InputPassword
            id="password"
            placeholder={PASSWORD_PLACEHOLDER}
            error={errors.password?.message}
            showStrength={true}
            showStrengthLabel={true}
            value={passwordValue}
            autoComplete="new-password"
            {...register('password')}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="confirm-password" className="text-sm font-medium">
            Confirm Password
          </label>
          <InputPassword
            id="confirm-password"
            placeholder={PASSWORD_PLACEHOLDER}
            error={errors.confirmPassword?.message}
            autoComplete="new-password"
            {...register('confirmPassword')}
          />
        </div>

        <AuthFormActions gap={3}>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isLoading}
            onClick={loadDemoData}
          >
            Use Demo Data
          </Button>
          <AuthFooterLinks
            prefix="Already have an account?"
            links={[{ to: '/login', label: 'Log in' }]}
          />
        </AuthFormActions>
      </form>
    </AuthLayout>
  )
}
